<script setup>
import { ref, reactive, watch, computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import axios from 'axios';
import { useToast } from 'primevue/usetoast';
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';
import Textarea from 'primevue/textarea';
import InputText from 'primevue/inputtext';
import { useConfirm } from 'primevue/useconfirm';

const props = defineProps({
  visible: { type: Boolean, required: true },
  block: { type: Object, default: null },
  providers: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:visible', 'updated', 'deleted', 'edit-series']);

const show = ref(props.visible);
const toast = useToast();
const confirm = useConfirm();
const loading = ref(false);

const isSeries = computed(() => !!(props.block?.provider_block_series_id || props.block?.series_uuid));

const form = reactive({
  title: '',
  notes: '',
  providerUuid: '',
  date: null,
  startTime: null,
  endTime: null,
});

const initForm = () => {
  if (!props.block) return;
  
  const parseAsLocal = (str) => {
    if (!str) return null;
    if (str instanceof Date) return str;
    let normalized = String(str).replace(' ', 'T').split('.')[0].replace('Z', '');
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
  };

  form.title = props.block.title || '';
  form.notes = props.block.notes || '';
  form.date = parseAsLocal(props.block.start_datetime);
  form.startTime = parseAsLocal(props.block.start_datetime);
  form.endTime = parseAsLocal(props.block.end_datetime);
  
  const pUuid = props.block.provider?.uuid || props.block.provider_uuid;
  if (pUuid) {
    form.providerUuid = pUuid;
  } else if (props.block.provider_id) {
    const p = props.providers.find(p => String(p.id) === String(props.block.provider_id));
    form.providerUuid = p ? p.uuid : '';
  } else {
    form.providerUuid = '';
  }
};

watch(
    () => [props.visible, props.block?.uuid || props.block?.id],
    (newVal, oldVal) => {
        const [isVisible, uuid] = newVal;
        const [oldVisible, oldUuid] = oldVal || [false, null];
        
        show.value = isVisible;
        if (isVisible && (uuid !== oldUuid || !oldVisible)) {
            initForm();
        }
    },
    { immediate: true }
);

watch(show, (v) => {
  emit('update:visible', v);
});

const extractError = (e, defaultMsg = 'Vorgang fehlgeschlagen') => {
    const data = e.response?.data;
    if (data?.errors) {
        const first = Object.values(data.errors).flat()[0];
        if (first) return first;
    }
    return data?.message || e.message || defaultMsg;
};

const formatDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const updateBlock = async () => {
  if (!form.date || !form.startTime || !form.endTime || !form.title) {
    toast.add({ severity: 'warn', summary: 'Warnung', detail: 'Bitte füllen Sie alle Pflichtfelder aus.', life: 3000 });
    return;
  }
  loading.value = true;
  try {
    const baseDate = form.date instanceof Date ? form.date : new Date(form.date);
    const startObj = form.startTime instanceof Date ? form.startTime : new Date(form.startTime);
    const endObj = form.endTime instanceof Date ? form.endTime : new Date(form.endTime);
    
    const finalStart = new Date(baseDate);
    finalStart.setHours(startObj.getHours(), startObj.getMinutes(), 0, 0);

    const finalEnd = new Date(baseDate);
    finalEnd.setHours(endObj.getHours(), endObj.getMinutes(), 0, 0);

    const payload = {
      title: form.title,
      notes: form.notes,
      provider_id: form.providerUuid,
      date: formatDate(baseDate).split(' ')[0],
      start_datetime: formatDate(finalStart),
      end_datetime: formatDate(finalEnd),
    };
    
    const uuid = props.block.uuid || props.block.id;
    await axios.put(`/management/provider-blocks/${uuid}`, payload);
    
    toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Blockierte Zeit erfolgreich aktualisiert', life: 3000 });
    emit('updated');
    show.value = false;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Fehler', detail: extractError(e, 'Aktualisierung fehlgeschlagen'), life: 5000 });
  } finally {
    loading.value = false;
  }
};

const deleteBlock = () => {
    confirm.require({
        message: 'Möchten Sie diese blockierte Zeit wirklich löschen?',
        header: 'Blockierung löschen',
        icon: 'pi pi-exclamation-triangle',
        rejectLabel: 'Abbrechen',
        acceptLabel: 'Löschen',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: async () => {
            try {
                const uuid = props.block.uuid || props.block.id;
                await axios.delete(`/management/provider-blocks/${uuid}`);
                toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Blockierte Zeit gelöscht', life: 3000 });
                emit('deleted');
                show.value = false;
            } catch (e) {
                toast.add({ severity: 'error', summary: 'Fehler', detail: 'Löschen fehlgeschlagen', life: 5000 });
            }
        }
    });
};
</script>

<template>
  <Dialog 
    v-model:visible="show" 
    modal 
    header="Blockierung Bearbeiten" 
    :pt="{
      header: { class: '!p-5 !border-b !border-gray-100 !items-center' },
      title: { class: '!text-base !font-bold !text-gray-900 !uppercase !tracking-tight' }
    }"
  >
    <div class="space-y-4">

      <!-- Series ID -->
      <div v-if="isSeries" class="grid grid-cols-12 gap-4 items-center">
        <label class="col-span-4 font-bold text-gray-600 text-sm">Serien-ID</label>
        <div class="col-span-8">
          <div class="text-xs text-gray-500 bg-gray-50 p-2 rounded border border-gray-200 truncate">
            {{ props.block?.series_uuid || props.block?.provider_block_series_id }}
          </div>
        </div>
      </div>
      
      <!-- Date Row -->
      <div class="grid grid-cols-12 gap-4 items-center">
        <label class="col-span-4 font-bold text-gray-600 text-sm">Datum *</label>
        <div class="col-span-8 flex gap-3 items-center">
          <div class="flex-1">
            <Calendar 
              v-model="form.date" 
              dateFormat="dd.mm.yy"
              class="w-full"
              inputClass="p-2 border border-gray-300 rounded-lg bg-gray-50 text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none text-sm w-full"
            />
          </div>
          <i class="pi pi-arrow-right text-transparent text-xs"></i>
          <div class="flex-1"></div>
        </div>
      </div>

      <!-- Time Row -->
      <div class="grid grid-cols-12 gap-4 items-center">
        <label class="col-span-4 font-bold text-gray-600 text-sm">Start / Ende *</label>
        <div class="col-span-8 flex gap-3 items-center">
          <div class="flex-1">
            <Calendar 
              v-model="form.startTime" 
              timeOnly 
              :stepMinute="15"
              class="w-full"
              inputClass="p-2 border border-gray-300 rounded-lg bg-gray-50 text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none text-sm w-full"
            />
          </div>
          <i class="pi pi-arrow-right text-gray-300 text-xs"></i>
          <div class="flex-1">
            <Calendar 
              v-model="form.endTime" 
              timeOnly 
              :stepMinute="15"
              class="w-full"
              inputClass="p-2 border border-gray-300 rounded-lg bg-gray-50 text-indigo-600 font-semibold focus:ring-2 focus:ring-black outline-none text-sm w-full"
            />
          </div>
        </div>
      </div>

      <!-- Title Input -->
      <div class="grid grid-cols-12 gap-4 items-center">
        <label class="col-span-4 font-bold text-gray-600 text-sm pt-2">Grund *</label>
        <div class="col-span-8">
          <InputText 
            v-model="form.title" 
            class="w-full !rounded-lg border-gray-300 focus:!ring-2 focus:!ring-black outline-none text-sm"
            placeholder="z.B. Mittagspause, Urlaub..."
          />
        </div>
      </div>

      <!-- Note Textarea -->
      <div class="grid grid-cols-12 gap-4">
        <label class="col-span-4 font-bold text-gray-600 text-sm pt-2">Notiz</label>
        <div class="col-span-8">
          <Textarea 
            v-model="form.notes" 
            rows="4" 
            class="w-full !p-3 !rounded-xl !bg-gray-50 focus:!ring-2 focus:!ring-black outline-none transition-all text-sm"
            placeholder="Optionale Details..."
            autoResize
          />
        </div>
      </div>

      <!-- Provider Row -->
      <div class="grid grid-cols-12 gap-4 items-center">
        <label class="col-span-4 font-bold text-gray-600 text-sm">Mitarbeiter</label>
        <div class="col-span-8">
          <Dropdown 
            v-model="form.providerUuid" 
            :options="providers" 
            optionLabel="name" 
            optionValue="uuid" 
            placeholder="Mitarbeiter wählen"
            class="w-full !rounded-lg"
          />
        </div>
      </div>

    </div>

    <template #footer>
      <div class="flex justify-between items-center w-full pt-4 border-t mt-4">
        <div class="flex gap-2">
            <button 
            type="button"
            @click="show = false" 
            class="text-gray-500 font-bold hover:!bg-sky-50 hover:!text-sky-600 transition-all px-6 py-2 rounded-full text-sm"
            >
            Abbrechen
            </button>
            <Button 
                label="Löschen" 
                icon="pi pi-trash" 
                class="p-button-danger p-button-text !text-[12px] !font-bold hover:!bg-red-50 !rounded-full !px-4" 
                @click="deleteBlock" 
            />
            <Button 
                v-if="isSeries"
                label="Serie bearbeiten" 
                icon="pi pi-calendar" 
                class="p-button-info p-button-text !text-[12px] !font-bold hover:!bg-blue-50 !rounded-full !px-4" 
                @click="() => { show = false; emit('edit-series'); }" 
            />
        </div>
        <Button 
          :loading="loading" 
          class="!bg-black !text-[#FFF053] !border-none px-8 py-3 rounded-xl font-bold shadow-lg flex items-center gap-3" 
          @click="updateBlock" 
        >
          <i class="pi pi-save text-lg"></i>
          <span class="uppercase tracking-widest text-sm">Speichern</span>
        </Button>
      </div>
    </template>
  </Dialog>
</template>
